/**
 * Slack channel picker — mockup Step 03.
 *
 * Renders the post-OAuth landing where the workspace admin chooses
 * channels to route notifications to. Reads the `install` query
 * param to know which `SlackInstall` we're working under. Loads the
 * channel list via the bot's `conversations.list`; loads existing
 * `SlackIntegration` rows so already-added channels show as "Added"
 * rather than offering a duplicate Add affordance.
 *
 * Search input is load-bearing — workspaces with hundreds of
 * channels are common and scrolling is not a tolerable picker UX.
 *
 * Private channels the bot is not a member of don't appear in the
 * backend response at all (the backend filters them); the visual
 * "/invite @ottotester" hint applies to private channels the bot
 * is in but the user hasn't added yet — that's the helpful case the
 * mockup highlights.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Input } from '@/components/ui'
import {
  slackAPI,
  SlackError,
  type SlackChannelOption,
  type SlackInstallSummary,
  type SlackIntegrationSummary,
  type WorkspaceSummary,
} from '@/lib/api'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { useConfirm } from '@/hooks/useConfirm'

interface SlackChannelPickerProps {
  workspace: WorkspaceSummary
}

export function NotificationsSlackChannelPicker({ workspace }: SlackChannelPickerProps) {
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const installId = searchParams.get('install') ?? ''

  const [install, setInstall] = useState<SlackInstallSummary | null>(null)
  const [channels, setChannels] = useState<SlackChannelOption[]>([])
  const [integrations, setIntegrations] = useState<SlackIntegrationSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [slackError, setSlackError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  /** Tracks the most-recently-added integration in THIS picker
   * session so the Done button can hand it back to the Notifications
   * page for the wizard auto-open. Reset to null when the customer
   * removes the channel before clicking Done. Pre-existing channels
   * (from earlier visits) don't qualify — only fresh adds chain into
   * the wizard. */
  const [lastAddedIntegrationId, setLastAddedIntegrationId] = useState<
    string | null
  >(null)
  const [toast, setToast] = useState<
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string; detail?: string }
    | null
  >(null)
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [disconnectBusy, setDisconnectBusy] = useState(false)
  // #1215: themed confirm gate on the per-row Remove. Previously a one-
  // click affordance — a misclick would silently drop the channel
  // mapping, leaving notifications dark until someone noticed.
  const confirm = useConfirm()

  const fetchAll = useCallback(async () => {
    if (!installId) {
      setLoadError('Missing install id in URL.')
      setLoading(false)
      return
    }
    setLoading(true)
    setLoadError(null)
    setSlackError(null)
    try {
      const [allInstalls, ints, chs] = await Promise.all([
        slackAPI.listInstalls(workspace._id),
        slackAPI.listIntegrations(workspace._id),
        slackAPI.listChannels(workspace._id, installId),
      ])
      const i = allInstalls.find((x) => x._id === installId) ?? null
      setInstall(i)
      setIntegrations(ints.filter((it) => it.slackInstallId === installId))
      setChannels(chs)
    } catch (e) {
      if (e instanceof SlackError && e.slackError) {
        setSlackError(e.slackError)
      }
      setLoadError(e instanceof Error ? e.message : 'Failed to load channels')
    } finally {
      setLoading(false)
    }
  }, [workspace._id, installId])

  useEffect(() => {
    void fetchAll()
  }, [fetchAll])

  const addedChannelIds = useMemo(
    () => new Set(integrations.map((i) => i.channelId)),
    [integrations],
  )

  const filteredPublic = useMemo(
    () =>
      channels
        .filter((c) => !c.is_private)
        .filter((c) => c.name.toLowerCase().includes(filter.toLowerCase())),
    [channels, filter],
  )
  const filteredPrivate = useMemo(
    () =>
      channels
        .filter((c) => c.is_private)
        .filter((c) => c.name.toLowerCase().includes(filter.toLowerCase())),
    [channels, filter],
  )

  const handleAdd = useCallback(
    async (c: SlackChannelOption) => {
      setBusyId(c.id)
      try {
        const created = await slackAPI.addIntegration(workspace._id, {
          installId,
          channelId: c.id,
          channelName: c.name,
          channelIsPrivate: c.is_private,
        })
        // Remember this add so Done can hand the integration id back
        // to the Notifications page for the wizard auto-open. Most
        // recent wins — if the customer adds two channels in this
        // session, the wizard opens for the second.
        setLastAddedIntegrationId(created._id)
        setToast({ kind: 'success', message: `Added #${c.name}.` })
        await fetchAll()
      } catch (e) {
        const err = e as Error & { code?: string; upgradeHint?: string }
        setToast({
          kind: 'error',
          message: err.upgradeHint ?? err.message ?? 'Failed to add channel',
        })
      } finally {
        setBusyId(null)
      }
    },
    [workspace._id, installId, fetchAll],
  )

  const handleRemove = useCallback(
    async (integrationId: string, channelName: string) => {
      const ok = await confirm({
        title: `Remove #${channelName} from Slack routing?`,
        description:
          "Notifications routed to this channel stop going out immediately. You can add it back later from this picker.",
        confirmLabel: 'Remove channel',
        destructive: true,
      })
      if (!ok) return
      setBusyId(integrationId)
      try {
        await slackAPI.removeIntegration(workspace._id, integrationId)
        // If the customer just added this channel then removed it,
        // the wizard chain no longer applies — drop the captured
        // integrationId.
        if (lastAddedIntegrationId === integrationId) {
          setLastAddedIntegrationId(null)
        }
        setToast({ kind: 'success', message: `Removed #${channelName}.` })
        await fetchAll()
      } catch (e) {
        setToast({
          kind: 'error',
          message: 'Failed to remove channel',
          detail: e instanceof Error ? e.message : String(e),
        })
      } finally {
        setBusyId(null)
      }
    },
    [confirm, workspace._id, fetchAll, lastAddedIntegrationId],
  )

  const handleDisconnect = useCallback(async () => {
    setDisconnectBusy(true)
    try {
      await slackAPI.disconnectInstall(workspace._id, installId)
      navigate(`/workspaces/${workspace._id}/settings/workspace/integrations`)
    } catch (e) {
      setToast({
        kind: 'error',
        message: 'Failed to disconnect',
        detail: e instanceof Error ? e.message : String(e),
      })
    } finally {
      setDisconnectBusy(false)
    }
  }, [workspace._id, installId, navigate])

  const backToSteady = useCallback(
    () => {
      // After #1153 the standalone Notifications page is gone — Slack
      // lives as a card on the consolidated Integrations tab. The
      // `wizardChannel` query param it used to consume (auto-open the
      // channel-routing dialog in wizard mode) doesn't apply on the
      // new tab; channel routing moved to each variant's Automation
      // page. The param is still appended for any future consumer
      // (and to keep the URL shape stable for analytics), but the new
      // Integrations tab ignores it.
      const base = `/workspaces/${workspace._id}/settings/workspace/integrations`
      if (lastAddedIntegrationId) {
        navigate(
          `${base}?wizardChannel=${encodeURIComponent(lastAddedIntegrationId)}`,
        )
      } else {
        navigate(base)
      }
    },
    [workspace._id, navigate, lastAddedIntegrationId],
  )

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading channels…</p>
  }
  if (loadError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive" role="alert">
          {loadError}
        </p>
        {slackError && (
          <p className="font-mono text-xs text-muted-foreground">{slackError}</p>
        )}
        <Button size="sm" variant="outline" onClick={backToSteady}>
          Back to Integrations
        </Button>
      </div>
    )
  }
  void params

  return (
    <div className="space-y-4">
      <nav className="text-sm text-muted-foreground">
        <Link
          to={`/workspaces/${workspace._id}/settings/workspace/integrations`}
          className="text-primary hover:underline"
        >
          Integrations
        </Link>{' '}
        / Choose channels
      </nav>

      <div>
        <h2 className="text-lg font-semibold">Choose Slack channels</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick channels for OttoTester to post into. You can add more later
          from the Integrations page.
        </p>
      </div>

      {toast && (
        <div
          role={toast.kind === 'error' ? 'alert' : 'status'}
          className={`flex items-start justify-between gap-3 rounded border px-3 py-2 text-sm ${
            toast.kind === 'error'
              ? 'border-destructive/50 bg-destructive/10 text-destructive'
              : 'border-green-300 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-300'
          }`}
        >
          <div>
            <div>{toast.message}</div>
            {(toast as { detail?: string }).detail && (
              <div className="mt-1 font-mono text-xs opacity-80">
                {(toast as { detail?: string }).detail}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="text-xs underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {install && (
        <div className="flex items-center gap-3 rounded border border-muted-foreground/20 bg-muted/30 px-3 py-2">
          <div
            className="flex h-8 w-8 items-center justify-center rounded font-mono text-sm font-semibold text-white"
            style={{ background: '#4a154b' }}
            aria-hidden="true"
          >
            {(install.slackTeamName?.[0] ?? '?').toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold">
              Connected to {install.slackTeamName}
            </div>
            <div className="text-xs text-muted-foreground">
              {install.slackTeamDomain
                ? `${install.slackTeamDomain}.slack.com`
                : 'Slack workspace'}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDisconnectOpen(true)}
            className="text-destructive hover:bg-destructive/10"
          >
            Disconnect
          </Button>
        </div>
      )}

      <Input
        type="search"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter channels…"
        aria-label="Filter channels"
        className="w-full text-sm"
        clearable
      />

      <section>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Public channels ({filteredPublic.length})
        </div>
        <ChannelList
          channels={filteredPublic}
          integrations={integrations}
          addedChannelIds={addedChannelIds}
          busyId={busyId}
          onAdd={handleAdd}
          onRemove={handleRemove}
        />
      </section>

      <section>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Private channels ({filteredPrivate.length})
        </div>
        {filteredPrivate.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No private channels visible to the bot. To route to a private
            channel, run{' '}
            <code className="rounded bg-muted px-1 font-mono text-[10px]">
              /invite @ottotester
            </code>{' '}
            in the channel on Slack first, then refresh this list.
          </p>
        ) : (
          <ChannelList
            channels={filteredPrivate}
            integrations={integrations}
            addedChannelIds={addedChannelIds}
            busyId={busyId}
            onAdd={handleAdd}
            onRemove={handleRemove}
          />
        )}
      </section>

      <div className="flex items-center justify-between border-t pt-4">
        <div className="text-xs text-muted-foreground">
          {integrations.length} channel{integrations.length === 1 ? '' : 's'} added
          {lastAddedIntegrationId
            ? ' · Next: choose which apps and run types post to it.'
            : ''}
        </div>
        <div className="flex gap-2">
          <Button
            variant={lastAddedIntegrationId ? 'default' : 'outline'}
            size="sm"
            onClick={backToSteady}
          >
            {lastAddedIntegrationId
              ? 'Next: configure routing →'
              : integrations.length > 0
                ? 'Done'
                : 'Skip — finish setup'}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        title="Disconnect Slack workspace?"
        description={
          install ? (
            <>
              Disconnecting <strong>{install.slackTeamName}</strong> will also
              remove every channel destination under it. You can reconnect
              from the Integrations page if you change your mind.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Disconnect"
        confirmVariant="destructive"
        onConfirm={handleDisconnect}
        busy={disconnectBusy}
      />
    </div>
  )
}

function ChannelList({
  channels,
  integrations,
  addedChannelIds,
  busyId,
  onAdd,
  onRemove,
}: {
  channels: SlackChannelOption[]
  integrations: SlackIntegrationSummary[]
  addedChannelIds: Set<string>
  busyId: string | null
  onAdd: (c: SlackChannelOption) => void
  onRemove: (integrationId: string, channelName: string) => void
}) {
  if (channels.length === 0) {
    return <p className="text-xs text-muted-foreground">No channels match the filter.</p>
  }
  return (
    <ul className="divide-y rounded border">
      {channels.map((c) => {
        const already = addedChannelIds.has(c.id)
        const existing = integrations.find((it) => it.channelId === c.id)
        const isPrivateNotMember = c.is_private && !c.is_member
        return (
          <li
            key={c.id}
            className={`flex items-center justify-between px-3 py-2 text-sm ${
              isPrivateNotMember ? 'opacity-60' : ''
            }`}
          >
            <div className="flex-1">
              <div className="flex items-center gap-1 font-mono">
                <span aria-hidden="true">{c.is_private ? '🔒' : '#'}</span>
                <span>{c.name}</span>
              </div>
              {isPrivateNotMember && (
                <div className="mt-0.5 text-[11px] italic text-muted-foreground">
                  Bot must be invited — run{' '}
                  <code className="rounded bg-muted px-1 font-mono text-[10px]">
                    /invite @ottotester
                  </code>{' '}
                  in Slack first
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {typeof c.num_members === 'number'
                ? `${c.num_members} member${c.num_members === 1 ? '' : 's'}`
                : ''}
            </div>
            <div className="ml-3 flex items-center gap-2">
              {already && existing ? (
                <>
                  <span className="rounded bg-green-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-700 dark:bg-green-950 dark:text-green-300">
                    Added
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(existing._id, c.name)}
                    disabled={busyId === existing._id}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={() => onAdd(c)}
                  disabled={isPrivateNotMember || busyId === c.id}
                >
                  {busyId === c.id ? 'Adding…' : 'Add'}
                </Button>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
